import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { Sound, SoundsService } from '@app/core/sounds.service';
import { AuthenticationService } from '@app/core';
import { FrameStatus, GameCanvasService } from '@app/_v2/games/game-canvas/services/game-canvas.service';
import { Subscription } from 'rxjs/Subscription';
import { ModalService } from '@app/shared/modal.service';
import { PlayerStatsService } from '@app/core/player-stats.service';
import { BalanceService } from '@app/core/balance.service';
import { filter } from 'rxjs/operators';
import { CURRENCY_OPTIONS } from '@app/core/constants';
import { PlayerService } from '@app/player.service';

export enum infoClasses {
  NONE = '',
  START = 'start',
  OPEN = 'open',
  HINT = 'hint',
  WIN = 'win',
  LOSE = 'lose',
}

@Component({
  selector: 'app-game-canvas',
  templateUrl: './game-canvas.component.html',
  styleUrls: ['./game-canvas.component.scss'],
})
export class GameCanvasComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
  @HostBinding('class.fullscreen') isFullscreen: boolean = false;

  rulesIcon: string = 'book-spells';
  fullscreenIcon: string = 'expand';
  soundIcon: string = 'volume-up';
  musicIcon: string = 'music';
  musicToggle: boolean = true;
  sounds: Sound[] = [];

  statsOn: boolean = this.stats._enabled;
  soundsOn: boolean = true;

  infoBarStart: string = 'Select your bet amount and click start hunting.';
  infoBarOpen: string = 'Select a chest to open.';
  infoBar: string = this.infoBarStart;
  infoClass: string = infoClasses.START;
  hiddenModeDisplay: boolean = false;
  displayTimeout: any;

  rulesModalOpen: boolean = false;
  hasBackgroundMusic: boolean = true;

  _infoSubscription: Subscription;
  _ruleOpenSubscription: Subscription;
  _hasBackgroundMusicSubscription: Subscription;

  public micro: boolean = this.balance.getMicroViewer();
  public multiplier: number = this.balance.getStakeSize();
  public fraction: number = 0;
  private microFraction: number = 2;
  private normalFraction: number = 5;
  private subs: Subscription = new Subscription();

  chipValue = false;
  decimal = false;

  public _chipValueEnabled: boolean = true;
  public _chipDecimalEnabled: boolean = true;
  public _hasGuide: boolean = true;

  constructor(
    public playerService: PlayerService,
    private stats: PlayerStatsService,
    public gameCanvasService: GameCanvasService,
    private soundsService: SoundsService,
    private modal: ModalService,
    public auth: AuthenticationService,
    public cdr: ChangeDetectorRef,
    public balance: BalanceService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this._infoSubscription = this.gameCanvasService.$displayInfo.subscribe(data => this.manageInfo(data));
    this._ruleOpenSubscription = this.gameCanvasService.$toggleRulesOpen.subscribe(data => {
      this.rulesModalOpen = data;
      this.toggleRules();
    });
    this._hasBackgroundMusicSubscription = this.gameCanvasService.$hasBackgroundMusic.subscribe(data => {
      this.hasBackgroundMusic = data;
    });
    if (!this.soundsService.soundEnabled) {
      this.soundIcon = 'volume-slash';
    }

    // const value = localStorage.getItem(this._storageKey);
    // if (value !== 'true') {
    //   this.muteMusic();
    // }
  }

  ngOnInit() {
    this.enableChipDecimalSetting(false);

    // if (document.addEventListener) {
    // document.addEventListener('webkitfullscreenchange', this.exitHandler.bind(this), false);
    // document.addEventListener('mozfullscreenchange', this.exitHandler.bind(this), false);
    // document.addEventListener('fullscreenchange', this.exitHandler.bind(this), false);
    // document.addEventListener('MSFullscreenChange', this.exitHandler.bind(this), false);
    // }
    this.displayTimeout = setTimeout(() => {
      this.hiddenModeDisplay = true;
    }, 6000); // hide-after-time => breakpoint

    this.subs.add(
      this.balance.currencyObservable().subscribe(currency => {
        this.buildFraction();
      })
    );

    this.subs.add(
      this.balance
        .microObservable()
        .pipe(filter((micro: boolean) => micro !== this.micro))
        .subscribe((micro: boolean) => {
          this.micro = micro;
          this.buildFraction();
        })
    );

    this.subs.add(
      this.balance
        .stakeObservable()
        .pipe(filter((stake: number) => stake !== this.multiplier))
        .subscribe((stake: number) => {
          this.multiplier = stake;
          this.buildFraction();
        })
    );
    this.buildFraction();
  }

  private buildFraction(): void {
    if (!this.playerService.microable()) {
      this.fraction = 0;
    } else {
      this.fraction = this.micro ? this.microFraction : this.normalFraction;
    }
  }

  printAmount(amount: number): string {
    const options = CURRENCY_OPTIONS[this.playerService.getCurrency()];
    const multiplier = Number('1' + '0'.repeat(options.visibleDecimals));
    if (this.playerService.microable()) {
      amount = this.micro ? amount * 1e3 : amount;
    }
    return (Math.floor(amount * multiplier) / multiplier).toString();
  }

  ngOnChanges() {
    this.cdr.detectChanges();
    this.subs.unsubscribe();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  public getCurrencyLabel(): string {
    if (this.playerService.microable()) {
      return (this.micro ? 'm' : '') + this.playerService.getCurrency().toUpperCase();
    }
    return this.playerService.getCurrency().toUpperCase();
  }

  ngOnDestroy() {
    if (this._infoSubscription) {
      this._infoSubscription.unsubscribe();
    }
    if (this._ruleOpenSubscription) {
      this._ruleOpenSubscription.unsubscribe();
    }
    if (this._hasBackgroundMusicSubscription) {
      this._hasBackgroundMusicSubscription.unsubscribe();
    }
    if (!!this.displayTimeout) {
      clearTimeout(this.displayTimeout);
      this.displayTimeout = null;
    }
    // document.removeEventListener('webkitfullscreenchange');
    // document.removeEventListener('mozfullscreenchange');
    // document.removeEventListener('fullscreenchange');
    // document.removeEventListener('MSFullscreenChange');
  }

  exitHandler = () => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document['mozFullScreen'] &&
      !document['msFullscreenElement']
    ) {
      this.fullscreenIcon = 'expand';
      this.isFullscreen = false;
    }
  };

  private manageInfo(data: any) {
    switch (data.status) {
      case FrameStatus.WIN:
        this.infoBar = data.info;
        this.infoClass = infoClasses.WIN;
        break;
      case FrameStatus.LOST:
        this.infoBar = data.info;
        this.infoClass = infoClasses.LOSE;
        break;
      case FrameStatus.OPEN:
        this.infoBar = this.infoBarOpen;
        this.infoClass = infoClasses.OPEN;
        break;
      case FrameStatus.HINT:
        this.infoBar = data.info;
        this.infoClass = infoClasses.HINT;
        break;
      case FrameStatus.ERROR:
        break;
    }
  }

  public openFairness() {
    this.modal.openFairness();
  }

  public toggleStats() {
    this.stats.toggleStats();
    this.statsOn = this.stats._enabled;
  }

  public emitRules() {
    if (this.rulesModalOpen) {
      this.gameCanvasService.emitRuleOpen(false);
    } else {
      this.gameCanvasService.emitRuleOpen(true);
    }
  }

  public toggleRules() {
    if (!this.rulesModalOpen) {
      this.rulesModalOpen = false;
      this.rulesIcon = 'book-spells';
    } else {
      this.rulesModalOpen = true;
      this.rulesIcon = 'book-open';
    }
  }

  public toggleSound() {
    this.soundsService.toggleSounds();
    if (this.soundsService.soundEnabled) {
      this.soundIcon = 'volume-up';
      this.soundsOn = false;
    } else {
      this.soundIcon = 'volume-slash';
      this.soundsOn = true;
    }
  }

  public muteMusic() {
    this.musicIcon = 'volume-mute';
    this.soundsService.mute('bg-music', true);
    this.musicToggle = false;
  }

  public toggleMusic() {
    if (this.musicToggle) {
      this.musicIcon = 'volume-mute';
      this.soundsService.mute('bg-music', true);
    } else {
      this.musicIcon = 'music';
      this.soundsService.mute('bg-music', false);
    }
    this.musicToggle = !this.musicToggle;
    // localStorage.setItem(this._storageKey, this.musicToggle.toString());
  }

  public toggleFullscreen() {
    let elem = document.getElementById('gameCanvas');

    if (!document.fullscreenElement) {
      this.fullscreenIcon = 'compress';
      this.isFullscreen = true;
      let methodToBeInvoked =
        elem.requestFullscreen ||
        elem.webkitRequestFullScreen ||
        elem['mozRequestFullscreen'] ||
        elem['msRequestFullscreen'];
      if (methodToBeInvoked) {
        methodToBeInvoked.call(elem);
      }
    } else {
      this.fullscreenIcon = 'expand';
      this.isFullscreen = false;
      let methodToBeInvoked =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document['mozCancelFullScreen'] ||
        document['msExitFullscreen'];
      if (methodToBeInvoked) {
        methodToBeInvoked.call(document);
      }
    }
  }

  setMicro(value: boolean, $event: Event): void {
    $event.preventDefault();
    this.balance.setMicroViewer(value);
  }

  setMultiplier(value: number, $event: Event): void {
    $event.preventDefault();
    this.balance.setStakeSize(value);
  }

  public enableChipValueSetting(value: boolean) {
    this._chipValueEnabled = value;
    if (!value) {
      this.balance.setStakeSize(1);
    }
    return this;
  }

  public enableChipDecimalSetting(value: boolean) {
    this._chipDecimalEnabled = value;
    this.balance.setMicroViewer(value);
    return this;
  }

  public hasGuide(value: boolean) {
    this._hasGuide = value;
    return this;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/_v2/games/game-canvas/game-canvas.component.ts